import { useState, useEffect, useContext, useRef } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttSelectMship,
    SttFormPrice,
    SttFormTextarea,
    SttSelectPForm,
} from '../../sporttia/all';
import { formatPriceForApi, parsePrice } from '../../lib/utils';
import POSStripePaymentDialog from '../stripe/POSStripePaymentDialog';
import DataphonePaymentDialog from './DataphonePaymentDialog';
import constants from '../../config/constants';
import CashdroControlDialog from './CashdroControlDialog';
import { PosPaymentParams } from '../../types/payment';

type Mship = {
    id?: number;
    name: string;
    fullName?: string;
    user: {
        id?: number;
    };
};

type Payment = {
    concept: string;
    price: string | number;
    mship: Mship;
    paymentForm: string;
};

type PaymentCreationDialogProps = {
    open: boolean;
    mship?: Mship | null;
    onClose: () => void;
    onPaymentSuccess?: (payment?: PaymentResponse['payment']) => void;
    targetAPI?: string;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type PaymentResponse = {
    payment?: {
        price: number;
        concept: string;
        paymentForm?: string;
        tpv?: {
            id: number;
            sc?: {
                id: number;
                short: string;
                customer?: {
                    name: string;
                    cif: string;
                };
                address: string;
                phonePublic: string;
            };
        };
    };
};

/**
 * Create payment
 *
 * Props
 *
 * 		* Object mship: mship por defecto
 * 		* Function onPaymentSuccess(payment)
 * 	    * String targetAPI (for example /bookings/1234/payments)
 */
export default function PaymentCreationDialog({
    open,
    mship,
    onClose,
    onPaymentSuccess,
    targetAPI,
}: PaymentCreationDialogProps): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [payment, setPayment] = useState<Payment>({
        concept: '',
        price: '',
        mship: mship || { name: '', user: {} },
        paymentForm: '',
    });

    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams | null>(null);
    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);
    // Un segundo clic mientras se crea el pago crearía otro pago (y otro TPV)
    const [creating, setCreating] = useState(false);
    const creatingRef = useRef(false);

    const CASHDRO = cxt?.sc?.cashdroConnected;

    useEffect(() => {
        setPayment({
            ...payment,
            mship: mship || { name: '', user: {} },
        });
    }, [mship]);

    /**
     * Handle form
     */
    const handleForm = ({ name, value }: FormChangeEvent): void => {
        if (!name) return;
        setPayment({ ...payment, [name]: value });
    };

    /**
     * Create payment
     */
    const createPayment = (): void => {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreating(true);

        cxt?.api('POST', targetAPI || '/payments', {
            loading: (isLoading: boolean) => {
                if (isLoading) return;
                creatingRef.current = false;
                setCreating(false);
            },
            params: {
                concept: payment.concept,
                price: parsePrice(payment.price),
                idUser: payment?.mship?.user?.id || null,
                paymentForm: payment.paymentForm,
            },
            success: (response: PaymentResponse) => {
                cxt.showMessage('S', t('Created'));

                setPayment({
                    concept: '',
                    price: '',
                    mship: mship || { name: '', user: {} },
                    paymentForm: '',
                });

                if (
                    response?.payment?.tpv &&
                    response?.payment?.paymentForm?.toUpperCase() ===
                        'DATAFONO_CONNECTED'
                ) {
                    setPosPaymentParams({
                        amount: response.payment.price,
                        concept: response.payment.concept,
                        idTpv: response.payment.tpv.id,
                        sc: {
                            id: response.payment.tpv?.sc?.id,
                            scName: response.payment.tpv?.sc?.short,
                            entityName:
                                response.payment.tpv?.sc?.customer?.name,
                            address: response.payment.tpv?.sc?.address,
                            cif: response.payment.tpv?.sc?.customer?.cif,
                            phone: response.payment.tpv?.sc?.phonePublic,
                        },
                        returnParams: undefined,
                    });
                    return;
                }

                onPaymentSuccess?.(response.payment);
            },
        });
    };

    /* Iniciar pago (puede quererse pagar con 'cashdro' / algún dispositivo físico antes de llamar a la API */
    const startPayment = (): void => {
        if (
            !(
                payment &&
                payment.mship &&
                payment.mship.user &&
                payment.concept &&
                payment.price
            )
        ) {
            cxt?.showMessage(
                'W',
                'Necesita especificar usuario, concepto y cantidad',
            );
            return;
        }

        if (
            CASHDRO &&
            payment.paymentForm === constants.payment.paymentForms.cashdro.name
        ) {
            setCashdroPayment(true);
            setCashdroPaymentAmount(formatPriceForApi(payment.price));
        } else {
            createPayment();
        }
    };

    const closeDialog = (): void => {
        setPayment({
            concept: '',
            price: '',
            mship: mship || { name: '', user: {} },
            paymentForm: '',
        });

        onClose();
    };

    // -----| Render |-----
    return (
        <>
            <SttDialog
                title={t('New')}
                open={open}
                onClose={onClose}
                content={
                    <Grid container spacing={3}>
                        <SttSelectMship
                            mship={
                                payment.mship as Parameters<
                                    typeof SttSelectMship
                                >[0]['mship']
                            }
                            onSelect={(selectedMship) =>
                                setPayment({
                                    ...payment,
                                    mship: selectedMship as Mship,
                                })
                            }
                        />

                        <SttFormTextarea
                            grid
                            name="concept"
                            caption={t('Concept')}
                            defVal={payment.concept}
                            onChange={handleForm}
                        />

                        <SttFormPrice
                            grid
                            md={4}
                            name="price"
                            caption={t('Price')}
                            defVal={String(payment.price)}
                            onChange={handleForm}
                        />

                        {/* --- Payment form --- */}
                        <SttSelectPForm
                            grid
                            md={8}
                            name="paymentForm"
                            caption={t('PaymentForm')}
                            defVal={payment.paymentForm}
                            onChange={handleForm}
                            activePFs={[
                                '',
                                'PURSE',
                                'CASH',
                                'BANK',
                                cxt?.sc?.dataphoneConnected
                                    ? 'DATAFONO_CONNECTED'
                                    : 'DATAFONO',
                                'RECEIPT',
                                'FREE',
                            ].concat(CASHDRO ? ['CASHDRO'] : [])}
                        />
                    </Grid>
                }
                buttons={[
                    {
                        caption: t('Create'),
                        type: 'accept',
                        onClick: () => startPayment(),
                        loading: creating,
                        disabled:
                            creating ||
                            !(payment && payment.mship && payment.mship.user),
                    },
                    {
                        type: 'close',
                        onClick: closeDialog,
                    },
                ]}
            />

            {/* stripePosPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={stripePosPaymentParams}
                    onClose={() => setStripePosPaymentParams(null)}
                    onSuccess={() => {
                        onPaymentSuccess?.();
                    }}
                />
            ) : null */}

            {cxt?.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        onPaymentSuccess?.();
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        onPaymentSuccess?.();
                    }}
                />
            )}

            {cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                    }}
                    onSuccess={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        createPayment();
                    }}
                />
            )}
        </>
    );
}
